import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, type AdminClient } from '../api/admin';
import { errorMessage, getAdmin, getSnapshot, importDictionary, saveAdmin, setSigner, uploadSnapshot, type AdminConfig, type AdminState, type ImportSummary } from '../api/uiSignoff';
import { readXlsx } from '../lib/xlsxRead';
import { parseDictionary, type DictionaryParse } from '../modules/ui-signoff/dictionary';
import PersonasEditor, { EMPTY_PERSONAS, ListInput } from './PersonasEditor';

// Staff configuration for the UI sign-off module: which repo and branch to read, who may sign,
// and what they sign. Reachable by direct URL, so the client is fetched and named on screen:
// saving another client's repo here would show one client's pages to another.

const DEFAULT_STATEMENT =
  'I confirm that the {object} record page as seen by {persona} for {record type}, built from {repo} at commit {sha7}, is correct for go-live, subject to the exceptions listed.';

const EMPTY: AdminConfig = {
  repo: '', baselineBranch: 'main', readableBranches: [], coreRepo: null, coreRef: null, coreRoot: null,
  enabledObjects: ['Campaign'], qaTrackerId: null, statementText: DEFAULT_STATEMENT,
  // New clients keep personas here, so their repos need no config file.
  personasConfig: EMPTY_PERSONAS,
  excludePaths: [],
  orgBranch: null,
  captureEnv: null,
};

const field: React.CSSProperties = { display: 'grid', gap: 4, fontSize: 13 };
const input: React.CSSProperties = { border: '1px solid var(--mi-border)', borderRadius: 6, padding: '6px 8px', font: 'inherit' };

export default function AdminModuleUiSignoff() {
  const { clientId } = useParams();
  const { getToken } = useAuth();
  const [client, setClient] = useState<AdminClient | null>(null);
  const [state, setState] = useState<AdminState | null>(null);
  const [cfg, setCfg] = useState<AdminConfig>(EMPTY);
  const [status, setStatus] = useState<string | null>(null);
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [uploadRef, setUploadRef] = useState('main');
  const [importObject, setImportObject] = useState('');
  const [parsed, setParsed] = useState<(DictionaryParse & { file: string }) | null>(null);
  const [preview, setPreview] = useState<ImportSummary | null>(null);
  const [importStatus, setImportStatus] = useState<string | null>(null);

  const readDictionary = async (file: File) => {
    setParsed(null); setPreview(null); setImportStatus(null);
    try {
      const object = importObject || cfg.enabledObjects[0];
      // Known fields come from the baseline snapshot, so a typo'd API name shows up before import.
      let fields: Record<string, unknown> = {};
      try { fields = (await getSnapshot(client!.slug, object, cfg.baselineBranch, await getToken())).object.fields; } catch { /* no snapshot yet: skip the check */ }
      const p = parseDictionary(await readXlsx(await file.arrayBuffer()), fields);
      if (!p) { setImportStatus('No sheet in that workbook has “API Name” and “Agreed layout” columns.'); return; }
      setParsed({ ...p, file: file.name });
      setPreview(await importDictionary(clientId!, { object, source: file.name, dryRun: true, decisions: p.decisions, questions: p.questions }, await getToken()));
    } catch (e) { setImportStatus(`Could not read it: ${errorMessage(e)}`); }
  };

  const reload = async () => {
    if (!clientId) return;
    const t = await getToken();
    const [s, c] = await Promise.all([getAdmin(clientId, t), adminApi.getClient(t, clientId)]);
    setState(s);
    setClient(c);
    if (s.config) setCfg({ ...s.config, personasConfig: s.config.personasConfig ?? null, excludePaths: s.config.excludePaths ?? [], orgBranch: s.config.orgBranch ?? null, captureEnv: s.config.captureEnv ?? null });
    setRoles(Object.fromEntries(s.signers.map((x) => [x.userId, x.roleLabel])));
  };
  useEffect(() => { reload().catch((e) => setStatus(errorMessage(e))); }, [clientId]);

  const set = <K extends keyof AdminConfig>(k: K, v: AdminConfig[K]) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setStatus(null);
    try {
      await saveAdmin(clientId!, { ...cfg, coreRepo: cfg.coreRepo || null, coreRef: cfg.coreRef || null, coreRoot: cfg.coreRoot || null, qaTrackerId: cfg.qaTrackerId || null, statementText: cfg.statementText || null }, await getToken());
      await reload();
      setStatus('Saved.');
    } catch (e) { setStatus(`Not saved: ${errorMessage(e)}`); }
  };

  const signerIds = new Set(state?.signers.map((s) => s.userId) ?? []);

  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 900 }}>
      <div>
        <Link to={`/admin/clients/${clientId}`} style={{ color: 'var(--mi-purple)', fontSize: 13 }}>← {client?.name ?? 'Client'}</Link>
        <h2 style={{ margin: '6px 0 0', color: 'var(--mi-navy)' }}>UI Sign-off: {client?.name ?? '…'}</h2>
        <p style={{ color: 'var(--mi-muted)', fontSize: 13, margin: '4px 0 0' }}>
          Record pages are rendered from the repo below, never from an org. {state && (state.canReadRepos
            ? 'The portal reads the repo through its GitHub App.'
            : 'The portal’s GitHub App is not configured, so only snapshots uploaded below are shown.')}
        </p>
      </div>

      <section style={{ display: 'grid', gap: 12, background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, padding: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Repository</h3>
        <label style={field}>Repo (owner/name)
          <input id="uis-repo" style={input} value={cfg.repo} onChange={(e) => set('repo', e.target.value.trim())} placeholder="Mount-Insights/clientverse-acme" />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
          <label style={field}>Baseline branch
            <input id="uis-base" style={input} value={cfg.baselineBranch} onChange={(e) => set('baselineBranch', e.target.value.trim())} />
          </label>
          <label style={field}>Other readable branches (comma-separated; * stops at a slash, ** does not)
            <ListInput id="uis-readable" value={cfg.readableBranches} onChange={(v) => set('readableBranches', v)} placeholder="qa, feature/*" />
          </label>
        </div>
        <label style={field}>Objects to review (API names, comma-separated)
          <ListInput id="uis-objects" value={cfg.enabledObjects} onChange={(v) => set('enabledObjects', v)} />
        </label>
        <label style={field}>Paths to leave out (optional; globs from the repo root, comma-separated)
          <ListInput id="uis-exclude" value={cfg.excludePaths} onChange={(v) => set('excludePaths', v)} placeholder="envs/*/deprecated/**" />
          <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>For metadata the repo keeps but no longer uses, such as retired record types that would otherwise show as views of their own.</span>
        </label>
        <label style={field}>The org's copy (optional; a branch; retired once Org captures is set)
          <input id="uis-org-branch" style={input} value={cfg.orgBranch ?? ''} onChange={(e) => set('orgBranch', e.target.value.trim() || null)} placeholder="mirror/acme--cvdev" />
          <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>A branch the repo keeps as a copy of an org, written by its own org sync. The page offers it as In the org, read-only, to compare with the branch. It is only as current as its last sync.</span>
        </label>
        <label style={field}>Org captures (optional; an environment)
          <input id="uis-capture-env" style={input} value={cfg.captureEnv ?? ''} onChange={(e) => set('captureEnv', e.target.value.trim().toLowerCase() || null)} placeholder="cvdev" />
          <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>The environment whose org captures the page shows as The org: tags capture/&lt;environment&gt;/… that the repo's org-capture workflow writes, each the org's metadata at one time. A capture is drawn with the Core version its org had installed, so the apps and personas must be kept on this page, with Core's namespace.</span>
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <label style={field}>Core repo
            <input id="uis-core-repo" style={input} value={cfg.coreRepo ?? ''} onChange={(e) => set('coreRepo', e.target.value.trim() || null)} placeholder="clientverse/cv-crm" />
          </label>
          <label style={field}>Core ref (tag or branch)
            <input id="uis-core-ref" style={input} value={cfg.coreRef ?? ''} onChange={(e) => set('coreRef', e.target.value.trim() || null)} placeholder="v1.1.8-7-core" />
          </label>
          <label style={field}>Core source directory
            <input id="uis-core-root" style={input} value={cfg.coreRoot ?? ''} onChange={(e) => set('coreRoot', e.target.value.trim() || null)} placeholder="packages/unlocked-core/force-app/main/default" />
          </label>
        </div>
      </section>

      <section style={{ display: 'grid', gap: 12, background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, padding: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Apps and personas</h3>
        <PersonasEditor value={cfg.personasConfig} onChange={(v) => set('personasConfig', v)} />
        <p style={{ margin: 0, fontSize: 12, color: 'var(--mi-muted)' }}>Saved with the button under the statement below.</p>
      </section>

      <section style={{ display: 'grid', gap: 12, background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, padding: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Questions and the sign-off statement</h3>
        <label style={field}>Q&amp;A tracker for questions raised on a page
          <select id="uis-tracker" style={input} value={cfg.qaTrackerId ?? ''} onChange={(e) => set('qaTrackerId', e.target.value || null)}>
            <option value="">None (questions off)</option>
            {state?.trackers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label style={field}>Statement a signer agrees to. {'{object}'}, {'{persona}'}, {'{record type}'}, {'{repo}'} and {'{sha7}'} are filled in.
          <textarea id="uis-statement" style={{ ...input, minHeight: 80 }} value={cfg.statementText ?? ''} onChange={(e) => set('statementText', e.target.value)} />
        </label>
        {state?.config && <p style={{ margin: 0, fontSize: 12, color: 'var(--mi-muted)' }}>Statement version {state.config.statementVersion}. Changing the wording starts a new version; each sign-off stores the exact text it was given.</p>}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button className="btn-primary" onClick={save} disabled={!cfg.repo}>Save</button>
          {status && <span style={{ fontSize: 13 }}>{status}</span>}
        </div>
      </section>

      <section style={{ display: 'grid', gap: 12, background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, padding: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Signers</h3>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--mi-muted)' }}>Only client users can sign. Staff can never sign for a client. Removing a signer keeps their past sign-offs.</p>
        <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr><th style={{ textAlign: 'left', padding: 6 }}>User</th><th style={{ textAlign: 'left', padding: 6 }}>Role they sign as</th><th /></tr></thead>
          <tbody>
            {state?.clientUsers.map((u) => (
              <tr key={u.userId} style={{ borderTop: '1px solid var(--mi-border)' }}>
                <td style={{ padding: 6 }}>{u.name ?? u.email}<div style={{ fontSize: 11, color: 'var(--mi-muted)' }}>{u.email}</div></td>
                <td style={{ padding: 6 }}>
                  <input id={`uis-role-${u.userId}`} aria-label={`Role for ${u.email}`} style={input} value={roles[u.userId] ?? ''} placeholder="e.g. Marketing lead"
                    onChange={(e) => setRoles((r) => ({ ...r, [u.userId]: e.target.value }))} />
                </td>
                <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                  <button disabled={!roles[u.userId]?.trim()} onClick={async () => { await setSigner(clientId!, u.userId, roles[u.userId].trim(), await getToken()); await reload(); }}>
                    {signerIds.has(u.userId) ? 'Update' : 'Make signer'}
                  </button>
                  {signerIds.has(u.userId) && <button style={{ marginLeft: 6 }} onClick={async () => { await setSigner(clientId!, u.userId, null, await getToken()); await reload(); }}>Remove</button>}
                </td>
              </tr>
            ))}
            {state && !state.clientUsers.length && <tr><td colSpan={3} style={{ padding: 6, color: 'var(--mi-muted)' }}>This client has no portal users yet.</td></tr>}
          </tbody>
        </table>
      </section>

      <section style={{ display: 'grid', gap: 12, background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, padding: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Import a field dictionary</h3>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--mi-muted)' }}>
          A workbook with one row per field and columns for API Name, Agreed layout, Confirmed, and a question and answer. Agreed layout and Confirmed become field decisions; questions go to the Q&amp;A tracker pinned to their field. Importing the same workbook again adds only what changed.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>Object{' '}
            <select id="uis-import-object" style={input} value={importObject || cfg.enabledObjects[0] || ''} onChange={(e) => { setImportObject(e.target.value); setParsed(null); setPreview(null); }}>
              {cfg.enabledObjects.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <input id="uis-import-file" type="file" accept=".xlsx" disabled={!state?.config || !client} onChange={async (e) => { const f = e.target.files?.[0]; if (f) await readDictionary(f); e.target.value = ''; }} />
        </div>
        {parsed && (
          <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
            <div>Read <b>{parsed.file}</b>, sheet “{parsed.sheet}”: {parsed.rows} fields, {parsed.decisions.length} decisions, {parsed.questions.length} questions.</div>
            {parsed.unknownFields.length > 0 && <div style={{ color: 'var(--mi-orange)' }}>Not in the repo at {cfg.baselineBranch}: {parsed.unknownFields.slice(0, 12).join(', ')}{parsed.unknownFields.length > 12 ? `, and ${parsed.unknownFields.length - 12} more` : ''}. Their decisions still import.</div>}
            {preview && <div>Importing adds {preview.decisions} decisions and {preview.questions} questions; {preview.skippedDecisions} decisions and {preview.skippedQuestions} questions are already recorded.</div>}
            <div>
              <button className="btn-primary" disabled={!preview || (preview.decisions === 0 && preview.questions === 0)} onClick={async () => {
                try {
                  const r = await importDictionary(clientId!, { object: importObject || cfg.enabledObjects[0], source: parsed.file, dryRun: false, decisions: parsed.decisions, questions: parsed.questions }, await getToken());
                  setImportStatus(`Imported ${r.decisions} decisions and ${r.questions} questions.`); setParsed(null); setPreview(null);
                } catch (e) { setImportStatus(`Import failed: ${errorMessage(e)}`); }
              }}>Import</button>
            </div>
          </div>
        )}
        {importStatus && <div style={{ fontSize: 13 }}>{importStatus}</div>}
      </section>

      <section style={{ display: 'grid', gap: 12, background: 'var(--mi-surface)', border: '1px solid var(--mi-border)', borderRadius: 8, padding: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Upload a snapshot</h3>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--mi-muted)' }}>
          For when the portal can’t read the repo. Produce it with <code>ui-snapshot extract … --commit &lt;sha&gt; --repo-name {cfg.repo || 'owner/name'}</code> from mi-delivery-ci, then pick the file. It becomes what the branch below shows.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>Branch <input id="uis-upload-ref" style={input} value={uploadRef} onChange={(e) => setUploadRef(e.target.value.trim())} /></label>
          <input id="uis-upload-file" type="file" accept="application/json,.json" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              const r = await uploadSnapshot(clientId!, uploadRef, JSON.parse(await file.text()), await getToken());
              setStatus(`Uploaded ${r.ref} @ ${r.commit.slice(0, 7)}.`);
            } catch (err) { setStatus(`Upload failed: ${errorMessage(err)}`); }
            e.target.value = '';
          }} />
        </div>
      </section>
    </div>
  );
}
